import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  centsToMoneyString,
  classifyVariance,
  moneyToCents,
  suggestShortageDenomination,
  type VarianceLevel,
} from '@fixflow/shared-types';
import {
  cashSessionsApi,
  addCashIn,
  addCashOut,
  closeCashSession,
  getCashSession,
  type CashEntry,
  type CashSession,
  type CloseCashSessionBody,
} from '@/lib/api/cash-sessions.js';
import { parseApiError } from '@/lib/api/errors.js';
import { formatCurrency, formatDateTime } from '@/lib/format.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { useManagerOverride } from '@/hooks/useManagerOverride.js';
import { ManagerOverrideModal } from '@/components/common/ManagerOverrideModal.js';
import {
  DenominationCounter,
  type DenominationCounterResult,
} from '@/components/cash/DenominationCounter.js';

const CASH_IN_REASONS = ['Petty cash received', 'Safe added', 'Till correction', 'Other'];
const CASH_OUT_REASONS = [
  'Petty cash expense',
  'Safe drop',
  'Bank deposit',
  'Till correction',
  'Other',
];

function currencyClass(value: string | number | null | undefined) {
  const amount = Number(value ?? 0);
  if (amount < 0) return 'text-red-600';
  if (amount > 0) return 'text-green-700';
  return 'text-slate-700';
}

function varianceUi(level: VarianceLevel | null | undefined) {
  if (level === 'balanced')
    return { className: 'text-green-700', label: 'Balanced' };
  if (level === 'minor')
    return { className: 'text-amber-700', label: 'Minor variance' };
  if (level === 'over')
    return { className: 'text-red-700', label: 'Over tolerance' };
  return { className: 'text-slate-700', label: '' };
}

function signedCurrency(value: string | number | null | undefined) {
  const amount = Number(value ?? 0);
  if (amount > 0) return `+${formatCurrency(amount)}`;
  return formatCurrency(amount);
}

function formatTime(value: string) {
  return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(
    new Date(value),
  );
}

function entryReason(entry: CashEntry) {
  if (entry.type === 'sale') return entry.ticketNumber ?? 'Sale';
  if (entry.type === 'refund') return entry.ticketNumber ?? 'Refund';
  return entry.reason ?? entry.type;
}

function entryBadge(entry: CashEntry) {
  if (entry.type === 'sale') return { label: 'SALE', className: 'bg-green-100 text-green-700' };
  if (entry.type === 'refund') return { label: 'REFUND', className: 'bg-red-100 text-red-700' };
  if (entry.type === 'cash_in') return { label: 'CASH IN', className: 'bg-blue-100 text-blue-700' };
  if (entry.type === 'cash_out')
    return { label: 'CASH OUT', className: 'bg-amber-100 text-amber-800' };
  return { label: entry.type.toUpperCase(), className: 'bg-slate-100 text-slate-700' };
}

function SummaryRow({
  label,
  value,
  emphasize = false,
  signed = false,
}: {
  label: string;
  value: string | number;
  emphasize?: boolean;
  signed?: boolean;
}) {
  return (
    <div
      className={`flex justify-between ${emphasize ? 'text-lg font-black text-slate-950' : 'text-sm'}`}
    >
      <span>{label}</span>
      <span className={currencyClass(value)}>
        {signed ? signedCurrency(value) : formatCurrency(value)}
      </span>
    </div>
  );
}

function DenomLines({
  title,
  lines,
}: {
  title: string;
  lines?: Array<{ label: string; qty: number; lineCents: number }>;
}) {
  if (!lines?.length) return null;
  return (
    <div className="space-y-1">
      <h4 className="text-xs font-black uppercase tracking-wide text-slate-500">{title}</h4>
      {lines.map((line) => (
        <div key={`${line.label}-${line.qty}`} className="flex justify-between text-sm">
          <span>
            {line.label} × {line.qty}
          </span>
          <span className="tabular-nums">{centsToMoneyString(line.lineCents)}</span>
        </div>
      ))}
    </div>
  );
}

function CashEntryForm({
  mode,
  sessionId,
  onDone,
}: {
  mode: 'cash_in' | 'cash_out';
  sessionId: string;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const reasons = mode === 'cash_in' ? CASH_IN_REASONS : CASH_OUT_REASONS;
  const mutation = useMutation({
    mutationFn: () => {
      const finalReason = reason === 'Other' ? customReason : reason;
      const body = { amount: Number(amount), reason: finalReason.trim() };
      return mode === 'cash_in' ? addCashIn(sessionId, body) : addCashOut(sessionId, body);
    },
    onSuccess: (session) => {
      queryClient.setQueryData(['cash-session-current'], session);
      toastSuccess(mode === 'cash_in' ? 'Cash in recorded' : 'Cash out recorded');
      onDone();
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  const finalReason = reason === 'Other' ? customReason : reason;
  return (
    <form
      className="space-y-3 rounded-xl bg-slate-50 p-4"
      onSubmit={(event) => {
        event.preventDefault();
        mutation.mutate();
      }}
    >
      <h3 className="font-bold text-slate-950">
        {mode === 'cash_in' ? 'Add Cash In' : 'Remove Cash Out'}
      </h3>
      <input
        className="w-full rounded-xl border border-slate-300 px-3 py-2"
        type="number"
        min="0.01"
        step="0.01"
        placeholder="Amount"
        value={amount}
        onChange={(event) => setAmount(event.target.value)}
      />
      <select
        className="w-full rounded-xl border border-slate-300 px-3 py-2"
        value={reason}
        onChange={(event) => setReason(event.target.value)}
      >
        <option value="">Select reason</option>
        {reasons.map((item) => (
          <option key={item} value={item}>
            {item}
          </option>
        ))}
      </select>
      {reason === 'Other' ? (
        <input
          className="w-full rounded-xl border border-slate-300 px-3 py-2"
          placeholder="Reason"
          value={customReason}
          onChange={(event) => setCustomReason(event.target.value)}
        />
      ) : null}
      <div className="flex justify-end gap-2">
        <button
          type="button"
          className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold"
          onClick={onDone}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!Number(amount) || !finalReason.trim() || mutation.isPending}
          className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
        >
          {mutation.isPending
            ? 'Saving...'
            : mode === 'cash_in'
              ? 'Add Cash In ✓'
              : 'Remove Cash Out ✓'}
        </button>
      </div>
    </form>
  );
}

function ZReport({ session }: { session: CashSession }) {
  const report = session.zReport;
  const tolerance = moneyToCents(session.storeCash?.cashVarianceTolerance ?? 5);
  const varianceLevel =
    session.varianceLevel ??
    (session.variance != null
      ? classifyVariance(moneyToCents(session.variance), tolerance)
      : null);
  const vUi = varianceUi(varianceLevel);

  return (
    <div className="printable-report space-y-4 rounded-2xl bg-white">
      <div>
        <h3 className="text-xl font-black text-slate-950">Z-REPORT</h3>
        <h3 className="text-lg font-black text-slate-950">{report?.storeName ?? 'Store'}</h3>
        <p className="text-sm text-slate-500">
          {formatDateTime(session.openedAt)} -{' '}
          {session.closedAt ? formatDateTime(session.closedAt) : 'Open'}
        </p>
        <p className="text-sm text-slate-500">Opened by: {session.openedByName ?? 'Unknown'}</p>
        {session.closedByName ? (
          <p className="text-sm text-slate-500">Closed by: {session.closedByName}</p>
        ) : null}
      </div>
      <div className="space-y-2 border-y border-slate-200 py-4">
        <h4 className="font-bold text-slate-950">Cash Summary</h4>
        <SummaryRow label="Opening float" value={session.summary.openingFloat} />
        <SummaryRow
          label={`Cash sales (${report?.cashSalesCount ?? 0})`}
          value={session.summary.cashSales}
          signed
        />
        <SummaryRow
          label={`Cash refunds (${report?.cashRefundsCount ?? 0})`}
          value={session.summary.cashRefunds}
          signed
        />
        <SummaryRow
          label={`Cash in (${report?.cashInCount ?? 0})`}
          value={session.summary.cashIn}
          signed
        />
        <SummaryRow
          label={`Cash out (${report?.cashOutCount ?? 0})`}
          value={session.summary.cashOut}
          signed
        />
        <SummaryRow
          label="Expected in drawer"
          value={session.expectedCash ?? session.summary.expectedCash}
          emphasize
        />
        <SummaryRow label="Physical count" value={session.closingCount ?? 0} />
        <div className={`flex justify-between text-lg font-black ${vUi.className}`}>
          <span>Variance</span>
          <span>{formatCurrency(session.variance ?? 0)}</span>
        </div>
        {vUi.label ? (
          <div className={`text-sm font-bold ${vUi.className}`}>{vUi.label}</div>
        ) : null}
        {session.closingNotes ? (
          <p className="text-sm text-slate-600">Note: {session.closingNotes}</p>
        ) : null}
        {session.openingCountMethod ? (
          <p className="text-xs text-slate-500">
            Open method: {session.openingCountMethod.replace('_', ' ')}
            {session.closingCountMethod
              ? ` · Close method: ${session.closingCountMethod.replace('_', ' ')}`
              : ''}
          </p>
        ) : null}
      </div>

      <DenomLines title="Opening count" lines={report?.openingDenominationLines} />
      <DenomLines title="Closing count" lines={report?.closingDenominationLines} />

      <div className="space-y-2">
        <h4 className="font-bold text-slate-950">Payment Breakdown</h4>
        <SummaryRow label="Cash" value={report?.paymentBreakdown.cash ?? 0} />
        <SummaryRow label="Card" value={report?.paymentBreakdown.card ?? 0} />
        <SummaryRow label="Store Credit" value={report?.paymentBreakdown.storeCredit ?? 0} />
        <SummaryRow
          label="Total Revenue"
          value={report?.paymentBreakdown.totalRevenue ?? 0}
          emphasize
        />
        <div className="flex justify-between text-sm">
          <span>Total Tickets</span>
          <span className="font-bold">{report?.paymentBreakdown.totalTickets ?? 0}</span>
        </div>
      </div>
    </div>
  );
}

export function CashSessionReportModal({
  sessionId,
  onClose,
}: {
  sessionId: string | null;
  onClose: () => void;
}) {
  const sessionQuery = useQuery({
    queryKey: ['cash-session', sessionId],
    queryFn: () => getCashSession(sessionId!),
    enabled: Boolean(sessionId),
  });

  if (!sessionId) return null;
  const session = sessionQuery.data;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4">
      <div className="max-h-[90vh] w-full max-w-xl overflow-auto rounded-2xl bg-white p-6 shadow-2xl">
        <div className="no-print mb-5 flex items-center justify-between">
          <h2 className="text-xl font-black text-slate-950">Session Z-Report</h2>
          <button className="rounded-lg p-2 text-slate-500 hover:bg-slate-100" onClick={onClose}>
            ×
          </button>
        </div>
        {sessionQuery.isLoading ? <div className="text-slate-500">Loading report...</div> : null}
        {session ? <ZReport session={session} /> : null}
        <div className="no-print mt-5 flex justify-end gap-2">
          <button
            className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold"
            onClick={() => window.print()}
          >
            🖨 Print Z-Report
          </button>
          <button
            className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export function CashDrawerModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [openingNotes, setOpeningNotes] = useState('');
  const [entryMode, setEntryMode] = useState<'cash_in' | 'cash_out' | null>(null);
  const [closingNotes, setClosingNotes] = useState('');
  const [closing, setClosing] = useState(false);
  const [closedSession, setClosedSession] = useState<CashSession | null>(null);
  const { requireOverride, modalProps: managerOverrideModal } = useManagerOverride();

  const sessionQuery = useQuery({
    queryKey: ['cash-session-current'],
    queryFn: cashSessionsApi.getCurrent,
    enabled: open,
    refetchInterval: open ? 60_000 : false,
  });

  const denomSettingsQuery = useQuery({
    queryKey: ['cash-denomination-settings'],
    queryFn: cashSessionsApi.getDenominationSettings,
    enabled: open,
  });

  const storeCash = sessionQuery.data?.storeCash ?? denomSettingsQuery.data ?? null;

  const openMutation = useMutation({
    mutationFn: (result: DenominationCounterResult) =>
      cashSessionsApi.open({
        countMethod: result.countMethod,
        ...(result.countMethod === 'denominations'
          ? { openingDenominations: result.denominations }
          : { openingFloat: result.totalMoney }),
        ...(openingNotes.trim() ? { notes: openingNotes.trim() } : {}),
        notifyManagers: result.notifyManagers,
      }),
    onSuccess: (session) => {
      setOpeningNotes('');
      queryClient.setQueryData(['cash-session-current'], session);
      toastSuccess(`Cash session opened — ${formatCurrency(session.openingFloat)} float`);
      onClose();
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  useEffect(() => {
    if (!open) {
      setClosedSession(null);
      setClosing(false);
      setEntryMode(null);
      setClosingNotes('');
      setOpeningNotes('');
    }
  }, [open]);

  const closeMutation = useMutation({
    mutationFn: ({
      session,
      body,
    }: {
      session: CashSession;
      body: CloseCashSessionBody;
    }) => closeCashSession(session.id, body),
    onSuccess: (session) => {
      setClosedSession(session);
      setClosing(false);
      queryClient.setQueryData(['cash-session-current'], null);
      queryClient.invalidateQueries({ queryKey: ['cash-sessions'] });
      toastSuccess('Cash session closed');
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  const liveClose = useMemo(() => {
    if (!sessionQuery.data || !storeCash) return null;
    return {
      expectedCents: moneyToCents(sessionQuery.data.summary.expectedCash),
      toleranceCents: moneyToCents(storeCash.cashVarianceTolerance),
      config: storeCash.cashDenominations,
    };
  }, [sessionQuery.data, storeCash]);

  async function handleCloseConfirm(session: CashSession, result: DenominationCounterResult) {
    if (!storeCash) return;
    const expectedCents = moneyToCents(session.summary.expectedCash);
    const varianceCents = result.totalCents - expectedCents;
    const level = classifyVariance(varianceCents, moneyToCents(storeCash.cashVarianceTolerance));

    if (level === 'over' && !closingNotes.trim()) {
      toastError('A note is required when variance exceeds store tolerance');
      return;
    }

    const body: CloseCashSessionBody = {
      countMethod: result.countMethod,
      ...(result.countMethod === 'denominations'
        ? { closingDenominations: result.denominations }
        : { closingCount: result.totalMoney }),
      ...(closingNotes.trim() ? { closingNotes: closingNotes.trim() } : {}),
      notifyManagers: result.notifyManagers,
    };

    if (level === 'over' && storeCash.cashVarianceRequiresOverride) {
      const override = await requireOverride({
        action: 'cash.session_close_variance',
        title: 'Close over-tolerance variance',
        description: `Variance of ${formatCurrency(varianceCents / 100)} exceeds the store limit of ${formatCurrency(storeCash.cashVarianceTolerance)}. Manager approval is required.`,
        requiredRole: 'store_manager',
        context: { amount: Math.abs(varianceCents / 100), reason: closingNotes.trim() },
      });
      if (!override.approved) return;
      body.managerOverrideAuthorizedBy = override.authorizedBy.id;
    }

    closeMutation.mutate({ session, body });
  }

  if (!open) return null;
  const session = sessionQuery.data;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4">
      <div className="max-h-[90vh] w-full max-w-lg overflow-auto rounded-2xl bg-white p-6 shadow-2xl">
        <div className="no-print mb-5 flex items-center justify-between">
          <h2 className="text-xl font-black text-slate-950">
            {closedSession
              ? '✓ Session Closed'
              : closing
                ? 'Close Cash Session'
                : session
                  ? '💵 Cash Drawer'
                  : 'Open Cash Session'}
          </h2>
          <button className="rounded-lg p-2 text-slate-500 hover:bg-slate-100" onClick={onClose}>
            ×
          </button>
        </div>

        {closedSession ? (
          <>
            <ZReport session={closedSession} />
            <div className="no-print mt-5 flex justify-end gap-2">
              <button
                className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold"
                onClick={() => window.print()}
              >
                🖨 Print Z-Report
              </button>
              <button
                className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white"
                onClick={onClose}
              >
                Done
              </button>
            </div>
          </>
        ) : sessionQuery.isLoading || (!session && denomSettingsQuery.isLoading) ? (
          <div className="text-slate-500">Loading cash drawer...</div>
        ) : !session && storeCash ? (
          <div className="space-y-4">
            <DenominationCounter
              title="Count Drawer — Opening"
              config={storeCash.cashDenominations}
              trackCoinRolls={storeCash.trackCoinRolls}
              changeAlertSettings={{
                changeAlertsEnabled: storeCash.changeAlertsEnabled,
                changeMinimums: storeCash.changeMinimums ?? {},
                largeBillThreshold: storeCash.largeBillThreshold,
                dropThreshold: storeCash.dropThreshold,
              }}
              confirmLabel="Confirm Opening"
              isPending={openMutation.isPending}
              onCancel={onClose}
              onConfirm={(result) => openMutation.mutate(result)}
            />
            <label className="block">
              <span className="text-sm font-medium text-slate-700">Notes (optional)</span>
              <textarea
                className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                value={openingNotes}
                onChange={(event) => setOpeningNotes(event.target.value)}
              />
            </label>
          </div>
        ) : !session ? (
          <div className="text-slate-500">Could not load denomination settings.</div>
        ) : closing && storeCash && liveClose ? (
          <div className="space-y-4">
            <DenominationCounter
              title="Count Drawer — Closing"
              config={storeCash.cashDenominations}
              trackCoinRolls={storeCash.trackCoinRolls}
              changeAlertSettings={{
                changeAlertsEnabled: storeCash.changeAlertsEnabled,
                changeMinimums: storeCash.changeMinimums ?? {},
                largeBillThreshold: storeCash.largeBillThreshold,
                dropThreshold: storeCash.dropThreshold,
              }}
              confirmLabel="Confirm Close"
              isPending={closeMutation.isPending}
              onCancel={() => setClosing(false)}
              onConfirm={(result) => {
                void handleCloseConfirm(session, result);
              }}
              footerExtra={({ totalCents }) => {
                const varianceCents = totalCents - liveClose.expectedCents;
                const level = classifyVariance(varianceCents, liveClose.toleranceCents);
                const vUi = varianceUi(level);
                const hint = suggestShortageDenomination(
                  varianceCents,
                  storeCash.cashDenominations,
                );
                return (
                  <div className="space-y-2 rounded-xl bg-slate-50 p-4 text-sm">
                    <div className="flex justify-between font-bold">
                      <span>COUNTED</span>
                      <span className="tabular-nums">{formatCurrency(totalCents / 100)}</span>
                    </div>
                    <SummaryRow label="Opening float" value={session.summary.openingFloat} />
                    <SummaryRow label="Cash sales" value={session.summary.cashSales} signed />
                    <SummaryRow label="Cash refunds" value={session.summary.cashRefunds} signed />
                    <SummaryRow label="Paid in" value={session.summary.cashIn} signed />
                    <SummaryRow label="Paid out" value={session.summary.cashOut} signed />
                    <div className="flex justify-between border-t border-slate-200 pt-2 font-black">
                      <span>EXPECTED</span>
                      <span className="tabular-nums">
                        {formatCurrency(liveClose.expectedCents / 100)}
                      </span>
                    </div>
                    <div className={`flex justify-between text-lg font-black ${vUi.className}`}>
                      <span>VARIANCE</span>
                      <span className="tabular-nums">
                        {formatCurrency(varianceCents / 100)}
                        {level === 'balanced' ? ' ✓' : ''}
                      </span>
                    </div>
                    <p className={`text-sm font-bold ${vUi.className}`}>{vUi.label}</p>
                    {hint ? (
                      <p className="text-xs text-amber-800">
                        Hint: shortage matches exactly one {hint.label}. Recount that denomination.
                      </p>
                    ) : null}
                    {level === 'over' ? (
                      <p className="text-xs text-red-700">
                        Over tolerance ({formatCurrency(storeCash.cashVarianceTolerance)}). Note
                        required
                        {storeCash.cashVarianceRequiresOverride
                          ? '; manager override required'
                          : ''}
                        .
                      </p>
                    ) : null}
                  </div>
                );
              }}
            />
            <label className="block">
              <span className="text-sm font-medium text-slate-700">
                Closing notes
                {liveClose ? ' (required when over tolerance)' : ''}
              </span>
              <textarea
                className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                value={closingNotes}
                onChange={(event) => setClosingNotes(event.target.value)}
              />
            </label>
          </div>
        ) : (
          <div className="space-y-5">
            <div className="text-sm text-slate-600">
              Session opened by {session.openedByName ?? 'Unknown'} at{' '}
              {formatDateTime(session.openedAt)}
              <br />
              Opening float: {formatCurrency(session.openingFloat)}
            </div>
            <div className="space-y-2 rounded-xl bg-slate-50 p-4">
              <h3 className="font-bold text-slate-950">Current Session Summary</h3>
              <SummaryRow label="Opening float" value={session.summary.openingFloat} />
              <SummaryRow label="Cash sales" value={session.summary.cashSales} signed />
              <SummaryRow label="Cash in" value={session.summary.cashIn} signed />
              <SummaryRow label="Cash out" value={session.summary.cashOut} signed />
              <SummaryRow label="Cash refunds" value={session.summary.cashRefunds} signed />
              <SummaryRow
                label="Expected in drawer"
                value={session.summary.expectedCash}
                emphasize
              />
            </div>
            <div>
              <h3 className="font-bold text-slate-950">Recent Entries</h3>
              <div className="mt-2 max-h-72 divide-y divide-slate-100 overflow-auto rounded-xl border border-slate-200">
                {(session.entries ?? []).slice(0, 10).map((entry) => {
                  const badge = entryBadge(entry);
                  return (
                    <div
                      key={entry.id}
                      className="grid grid-cols-[72px_88px_1fr_auto] items-center gap-3 px-3 py-2 text-sm"
                    >
                      <span className="text-slate-500">{formatTime(entry.createdAt)}</span>
                      <span
                        className={`rounded-full px-2 py-1 text-center text-[10px] font-black ${badge.className}`}
                      >
                        {badge.label}
                      </span>
                      <span className="font-medium text-slate-800">{entryReason(entry)}</span>
                      <span className={currencyClass(entry.amount)}>
                        {signedCurrency(entry.amount)}
                      </span>
                    </div>
                  );
                })}
                {!session.entries?.length ? (
                  <div className="px-3 py-4 text-sm text-slate-500">No entries yet.</div>
                ) : null}
              </div>
            </div>
            {entryMode ? (
              <CashEntryForm
                mode={entryMode}
                sessionId={session.id}
                onDone={() => setEntryMode(null)}
              />
            ) : null}
            <div className="flex flex-wrap gap-2">
              <button
                className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold"
                onClick={() => setEntryMode('cash_in')}
              >
                + Cash In
              </button>
              <button
                className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold"
                onClick={() => setEntryMode('cash_out')}
              >
                - Cash Out
              </button>
              <button
                className="ml-auto rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white"
                onClick={() => setClosing(true)}
              >
                🔒 Close Session & Count Cash
              </button>
            </div>
          </div>
        )}
      </div>
      {managerOverrideModal ? <ManagerOverrideModal {...managerOverrideModal} /> : null}
    </div>
  );
}
